/**
 * UserApiKeysSettings — Settings → API Keys panel. One row per managed
 * provider, lets the user add / replace / remove a personal key. Personal
 * keys override the org-level Secret Manager key when set.
 *
 * Mirrors the AdminPanel SystemHealth card visually but operates on the
 * user's own keys, not org secrets. We only ever show the last 4 chars of
 * a stored key so the user can verify which one is set without exposing it.
 */
import React, { useState } from 'react';
import { useStore } from '../store';
import { API_PROVIDERS, type ApiProviderDef } from '../config/apiProviders';
import { TestAndLinkKeyDialog } from './siterag/TestAndLinkKeyDialog';
import { Key, X, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const UserApiKeysSettings: React.FC<Props> = ({ open, onClose }) => {
  const userApiKeys = useStore(s => s.userApiKeys);
  const setUserApiKeys = useStore(s => s.setUserApiKeys);
  const [editingProvider, setEditingProvider] = useState<ApiProviderDef | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ApiProviderDef | null>(null);

  if (!open) return null;

  const handleRemove = (provider: ApiProviderDef) => {
    const next = { ...userApiKeys };
    delete next[provider.userKeyField];
    setUserApiKeys(next);
    toast.success(`${provider.displayName} key removed`);
    setConfirmRemove(null);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60"
        onClick={onClose}
        data-testid="api-keys-overlay"
      >
        <div
          className="bg-background w-[calc(100vw-2rem)] max-w-[640px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border rounded-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-border flex items-start gap-3">
            <div className="w-9 h-9 border border-border bg-state-hover flex items-center justify-center shrink-0">
              <Key className="w-4 h-4 text-text-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">
                Your API Keys
              </h2>
              <p className="text-[10px] text-text-secondary mt-0.5">
                Set personal keys for any provider. When set, your reports use your key
                instead of the org-level Secret Manager key. We test before linking and
                never log key values.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-text-muted hover:text-text-primary"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Provider rows */}
          <div className="px-5 py-4 space-y-2">
            {Object.values(API_PROVIDERS).map(provider => {
              const stored = userApiKeys[provider.userKeyField];
              const isSet = typeof stored === 'string' && stored.length > 0;
              const tail = isSet ? stored.slice(-4) : null;
              return (
                <div
                  key={provider.id}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border border-border bg-state-hover px-3 py-2.5"
                  data-testid={`api-key-row-${provider.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
                      {provider.displayName}
                    </div>
                    <div className="text-[10px] text-text-muted mt-0.5 font-mono">
                      {isSet ? `Personal key set (•••${tail})` : 'Using org key'}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={provider.signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[9px] uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`api-key-signup-${provider.id}`}
                    >
                      Get key <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                    <button
                      onClick={() => setEditingProvider(provider)}
                      className="text-[9px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-2 py-1 border border-border hover:bg-state-active"
                      data-testid={`api-key-edit-${provider.id}`}
                    >
                      {isSet ? 'Replace' : 'Add'}
                    </button>
                    {isSet && (
                      <button
                        onClick={() => setConfirmRemove(provider)}
                        className="text-[9px] font-bold uppercase tracking-widest text-text-secondary hover:text-danger px-2 py-1 border border-border hover:bg-state-active"
                        data-testid={`api-key-remove-${provider.id}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="text-[10px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-3 py-1.5 border border-border hover:bg-state-hover"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {/* Edit / add dialog */}
      {editingProvider && (
        <TestAndLinkKeyDialog
          provider={editingProvider}
          open
          onClose={() => setEditingProvider(null)}
          onLinked={() => setEditingProvider(null)}
        />
      )}

      {/* Remove-confirmation overlay */}
      {confirmRemove && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
          onClick={() => setConfirmRemove(null)}
          data-testid="api-key-remove-confirm"
        >
          <div
            className="bg-background w-[calc(100vw-2rem)] max-w-[420px] shadow-2xl border border-border rounded-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-start gap-3">
              <div className="w-9 h-9 border border-warning/40 bg-warning/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-warning" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold uppercase tracking-widest text-text-primary">
                  Remove {confirmRemove.displayName} key?
                </h3>
                <p className="text-[10px] text-text-secondary mt-0.5">
                  The org-level key will be used for new reports.
                </p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmRemove(null)}
                className="text-[10px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-3 py-1.5 border border-border hover:bg-state-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemove(confirmRemove)}
                className="text-[10px] font-bold uppercase tracking-widest text-white bg-danger hover:bg-danger/90 px-3 py-1.5 border border-danger"
                data-testid="api-key-remove-confirm-button"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
